/**
 * Cropper.js
 * https://github.com/jamesssooi/Croppr.js
 *
 * A JavaScript image cropper that's lightweight, awesome, and has
 * zero dependencies.
 *
 * (C) 2017 James Ooi. Released under the MIT License.
 */
/**
 * Modified by Yujie Liu to make it work for everything that has known width and height.
 */
import CropperCore from './core';

/**
 * This class is a wrapper for CropprCore that merely implements the main
 * interfaces for the Croppr instance. Look into CropprCore for all the
 * main logic.
 */
export default class Cropper extends CropperCore {
  getValue(mode) {
    return super.getValue(mode);
  }

  destroy() {
    return super.destroy();
  }

  /**
   * Moves the crop region to a specified coordinate.
   * @param {Number} x target x
   * @param {Number} y target y
   * @returns {void}
   */
  moveTo(x, y) {
    this.box.move(x, y);
    this.redraw();

    // Call the callback
    if (this.options.onCropEnd !== null) {
      this.options.onCropEnd(this.getValue());
    }
    return this;
  }

  /**
   * Resizes the crop region to a specified width and height.
   * @param {Number} width target width
   * @param {Number} height target height
   * @param {Array} origin The origin point to resize from.
   *      Defaults to [0.5, 0.5] (center).
   * @returns {void}
   */
  resizeTo(width, height, origin = [0.5, 0.5]) {
    this.box.resize(width, height, origin);
    this.redraw();

    // Call the callback
    if (this.options.onCropEnd !== null) {
      this.options.onCropEnd(this.getValue());
    }
    return this;
  }

  /**
   * Scale the crop region by a factor.
   * @param {Number} factor scale factor
   * @param {Array} origin The origin point to resize from.
   *      Defaults to [0.5, 0.5] (center).
   * @returns {Cropper} itself
   */
  scaleBy(factor, origin = [0.5, 0.5]) {
    this.box.scale(factor, origin);
    this.redraw();

    // Call the callback
    if (this.options.onCropEnd !== null) {
      this.options.onCropEnd(this.getValue());
    }
    return this;
  }

  /**
   * Resets the crop region to the initial settings.
   * @returns {Cropper} itself
   */
  reset() {
    this.box = this.initializeBox(this.options);
    this.redraw();

    // Call the callback
    if (this.options.onCropEnd !== null) {
      this.options.onCropEnd(this.getValue());
    }
    return this;
  }
}
